import jsPDF from "jspdf";
import { drawProHeader, drawProFooter, getSettingsCached } from "./pdfHeader";

const CONDITION_LABELS = {
  hta: "Hipertensión Arterial (HTA)",
  tiroides: "Alteraciones Tiroides (Hipo/Hiper)",
  ansiedad: "Ansiedad / Estrés severo",
  dm: "Diabetes Mellitus (DM I/II)",
  autoinmune: "Enfermedad Autoinmune",
  anemia: "Anemia / Déficit de Hierro",
  dislipemia: "Dislipemia / Colesterol alto",
  cardio: "Cardiopatía / Arritmias",
  alopecia: "Alopecia / Antecedente Familiar",
};

const TRI_SYMPTOM_LABELS = {
  caida: "Caída difusa / Aguda (Efluvio)",
  picores: "Picores / Escamación / Caspa",
  densidad: "Pérdida de densidad / Clareo",
  dolor: "Dolor / Sensibilidad (Tricodinia)",
  placas: "Placas alopécicas (Areata)",
  seborrea: "Exceso de grasa / Seborrea",
};

const TRI_TX_LABELS = { minoxidil: "Minoxidil", finasteride: "Finasteride", prp: "PRP" };

function box(doc, x, y, checked) {
  doc.setDrawColor(100, 116, 139);
  doc.setLineWidth(0.5);
  doc.rect(x, y - 6.5, 7, 7);
  if (checked) {
    doc.setDrawColor(15, 118, 110);
    doc.setLineWidth(1.2);
    doc.line(x + 1.2, y - 3.5, x + 3, y - 1);
    doc.line(x + 3, y - 1, x + 6, y - 6);
    doc.setDrawColor(100, 116, 139);
    doc.setLineWidth(0.5);
  }
}

function calcAge(dob) {
  if (!dob) return "";
  try {
    const d = new Date(dob);
    const age = Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000));
    return age > 0 && age < 130 ? String(age) : "";
  } catch { return ""; }
}

export async function generateInterrogationPDF(it) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const margin = 40;
  const settings = it._settings || (await getSettingsCached());
  const doctor = it._doctor || {
    name: it.doctor_name, specialty: it.doctor_specialty, license_number: it.doctor_license,
  };

  let y = await drawProHeader(doc, {
    docTitle: "HISTORIA CLÍNICA / INTERROGATORIO",
    docSubtitle: it.area ? `Área: ${it.area}` : "",
    settings, doctor,
  });
  y += 12;

  // Code / date header row
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(`Código: ${it.code}   ·   Verificación: ${it.verification_code}`, margin, y);
  const created = new Date(it.created_at);
  const dateStr = created.toLocaleDateString("es-ES");
  const timeStr = created.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  doc.text(`Fecha: ${dateStr}   Hora: ${timeStr}`, W - margin, y, { align: "right" });
  y += 14;

  // Section helper
  const section = (num, title) => {
    doc.setFillColor(15, 118, 110);
    doc.rect(margin, y - 10, W - 2 * margin, 16, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(`${num}. ${title}`, margin + 8, y);
    y += 14;
    doc.setTextColor(15, 23, 42);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
  };

  const field = (label, value, colWidth) => {
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(8);
    doc.text(label, margin, y);
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(10);
    const val = String(value || "").trim() || "—";
    const lines = doc.splitTextToSize(val, (colWidth || (W - 2 * margin)));
    doc.text(lines, margin, y + 12);
    y += 12 + lines.length * 12 + 4;
  };

  const twoCol = (l1, v1, l2, v2) => {
    const colW = (W - 2 * margin) / 2 - 6;
    const startY = y;
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(8);
    doc.text(l1, margin, y);
    doc.text(l2, margin + colW + 12, y);
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(10);
    const v1s = doc.splitTextToSize(String(v1 || "").trim() || "—", colW);
    const v2s = doc.splitTextToSize(String(v2 || "").trim() || "—", colW);
    doc.text(v1s, margin, startY + 12);
    doc.text(v2s, margin + colW + 12, startY + 12);
    const rowH = 12 + Math.max(v1s.length, v2s.length) * 12 + 4;
    y = startY + rowH;
  };

  // ==== 1. Datos de identificación
  section("1", "DATOS DE IDENTIFICACIÓN DEL PACIENTE");
  twoCol("Nombre y Apellidos", it.patient_name, "DNI / NIE / Pasaporte", it.patient_nif);
  twoCol("Teléfono", it.patient_phone, "Email", it.patient_email && !String(it.patient_email).endsWith("@nolinked.local") ? it.patient_email : "");
  twoCol("Ocupación", it.occupation, "F. Nacimiento", it.patient_dob);
  const age = calcAge(it.patient_dob);
  twoCol("Edad", age ? `${age} años` : "", "Área de consulta", it.area);
  y += 4;

  // ==== 2. Motivo
  section("2", "MOTIVO DE CONSULTA Y ANAMNESIS DE LA ENFERMEDAD ACTUAL");
  field("Motivo Principal de Consulta", it.chief_complaint);
  field("Historia / Evolución (inicio, síntomas asociados, factores agravantes/atenuantes)", it.history_evolution);

  // ==== 3. Antecedentes / alergias / hábitos
  section("3", "ANTECEDENTES PERSONALES, ALERGIAS Y HÁBITOS");
  const conds = new Set(it.conditions || []);
  const condIds = Object.keys(CONDITION_LABELS);
  const colGap = (W - 2 * margin) / 3;
  condIds.forEach((cid, idx) => {
    const col = idx % 3, row = Math.floor(idx / 3);
    const x = margin + col * colGap;
    const yy = y + row * 14;
    box(doc, x, yy, conds.has(cid));
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(9);
    doc.text(CONDITION_LABELS[cid], x + 11, yy);
  });
  y += Math.ceil(condIds.length / 3) * 14 + 6;

  // Alergias / tratamiento
  doc.setTextColor(100, 116, 139);
  doc.setFontSize(8);
  doc.text("Alergias Conocidas", margin, y);
  box(doc, margin + 100, y, !it.allergies_known);
  doc.setTextColor(15, 23, 42); doc.setFontSize(9);
  doc.text("No conocidas", margin + 112, y);
  box(doc, margin + 180, y, !!it.allergies_known);
  doc.text("Sí:", margin + 192, y);
  doc.setFontSize(10);
  const alergTxt = doc.splitTextToSize(it.allergies_detail || "—", W - margin - (margin + 210));
  doc.text(alergTxt, margin + 210, y);
  y += Math.max(12, alergTxt.length * 12) + 6;

  field("Tratamiento Habitual", it.habitual_treatment);
  field("Cirugías", it.surgeries);
  // Hábitos
  doc.setTextColor(100, 116, 139); doc.setFontSize(8);
  doc.text("Tabaco", margin, y);
  box(doc, margin + 44, y, it.smoking === "si"); doc.setTextColor(15, 23, 42); doc.setFontSize(9); doc.text("Sí", margin + 56, y);
  box(doc, margin + 78, y, it.smoking === "no"); doc.text("No", margin + 90, y);
  doc.setTextColor(100, 116, 139); doc.setFontSize(8);
  doc.text("Alcohol", margin + 140, y);
  box(doc, margin + 186, y, it.alcohol === "ocasional"); doc.setTextColor(15, 23, 42); doc.setFontSize(9); doc.text("Ocasional", margin + 198, y);
  box(doc, margin + 258, y, it.alcohol === "diario"); doc.text("Diario", margin + 270, y);
  box(doc, margin + 314, y, it.alcohol === "no"); doc.text("No", margin + 326, y);
  y += 16;

  // ==== 4. Tricología
  if ((it.tri_symptoms || []).length || it.tri_evolution_time || (it.tri_previous_tx || []).length || it.tri_density || it.tri_pull_test || it.tri_previous_tx_other) {
    section("4", "EVALUACIÓN TRICOLÓGICA ESPECIALIZADA (SALUD CAPILAR)");
    const syms = new Set(it.tri_symptoms || []);
    const symIds = Object.keys(TRI_SYMPTOM_LABELS);
    symIds.forEach((sid, idx) => {
      const col = idx % 2, row = Math.floor(idx / 2);
      const x = margin + col * ((W - 2 * margin) / 2);
      const yy = y + row * 14;
      box(doc, x, yy, syms.has(sid));
      doc.setTextColor(15, 23, 42); doc.setFontSize(9);
      doc.text(TRI_SYMPTOM_LABELS[sid], x + 11, yy);
    });
    y += Math.ceil(symIds.length / 2) * 14 + 6;
    field("Tiempo de evolución", it.tri_evolution_time);
    // Previous tx
    doc.setTextColor(100, 116, 139); doc.setFontSize(8);
    doc.text("Tratamientos previos", margin, y);
    y += 12;
    const tx = new Set(it.tri_previous_tx || []);
    let tx_x = margin;
    Object.entries(TRI_TX_LABELS).forEach(([id, lbl]) => {
      box(doc, tx_x, y, tx.has(id));
      doc.setTextColor(15, 23, 42); doc.setFontSize(9);
      doc.text(lbl, tx_x + 11, y);
      tx_x += 80;
    });
    doc.setTextColor(100, 116, 139); doc.setFontSize(8);
    doc.text("Otro:", tx_x, y);
    doc.setTextColor(15, 23, 42); doc.setFontSize(9);
    doc.text(it.tri_previous_tx_other || "—", tx_x + 26, y);
    y += 14;
    // Exploración
    doc.setTextColor(100, 116, 139); doc.setFontSize(8);
    doc.text("Densidad", margin, y);
    box(doc, margin + 50, y, it.tri_density === "normal"); doc.setTextColor(15, 23, 42); doc.setFontSize(9); doc.text("Normal", margin + 62, y);
    box(doc, margin + 108, y, it.tri_density === "disminuida"); doc.text("Disminuida", margin + 120, y);
    doc.setTextColor(100, 116, 139); doc.setFontSize(8);
    doc.text("Pull test", margin + 220, y);
    box(doc, margin + 262, y, it.tri_pull_test === "positivo"); doc.setTextColor(15, 23, 42); doc.setFontSize(9); doc.text("Positivo (+)", margin + 274, y);
    box(doc, margin + 336, y, it.tri_pull_test === "negativo"); doc.text("Negativo (−)", margin + 348, y);
    y += 16;
  }

  // ==== 5. Constantes vitales
  if (y > 720) { doc.addPage(); y = 50; }
  section("5", "CONSTANTES VITALES Y EXPLORACIÓN FÍSICA");
  const cellW = (W - 2 * margin) / 5;
  const drawCell = (i, label, val, unit) => {
    const cx = margin + i * cellW;
    doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.6);
    doc.rect(cx, y - 4, cellW - 4, 42);
    doc.setTextColor(100, 116, 139); doc.setFontSize(7);
    doc.text(label, cx + 6, y + 6);
    doc.setTextColor(15, 23, 42); doc.setFont("times", "bold"); doc.setFontSize(15);
    doc.text(val || "—", cx + 6, y + 26);
    doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(100, 116, 139);
    doc.text(unit, cx + 6, y + 36);
  };
  const pa = (it.bp_systolic && it.bp_diastolic) ? `${it.bp_systolic}/${it.bp_diastolic}` : "";
  drawCell(0, "P. ARTERIAL", pa, "mmHg");
  drawCell(1, "FREC. CARDÍACA", it.heart_rate ? String(it.heart_rate) : "", "bpm");
  drawCell(2, "SAT. O₂", it.o2_sat ? String(it.o2_sat) : "", "%");
  drawCell(3, "TEMPERATURA", it.temperature ? String(it.temperature) : "", "°C");
  const gp = (it.glucose && it.weight_kg) ? `${it.glucose}/${it.weight_kg}` : (it.glucose ? String(it.glucose) : (it.weight_kg ? String(it.weight_kg) : ""));
  drawCell(4, "GLUCEMIA / PESO", gp, "mg/dL · kg");
  y += 52;
  field("Hallazgos Exploración Física / MUE", it.physical_findings);

  // ==== 6. Impresión y plan
  if (y > 660) { doc.addPage(); y = 50; }
  section("6", "IMPRESIÓN DIAGNÓSTICA Y PLAN DE TRATAMIENTO");
  field("Juicio Clínico / Diagnóstico", it.diagnosis);
  field("Plan Terapéutico, Prescripción Médica e Indicaciones", it.treatment_plan);

  // Footer with ALIGNED signatures: patient LEFT, doctor RIGHT, QR + eIDAS in the center
  const verifyUrl = `${window.location.origin}/verify/${it.code}?v=${it.verification_code || ""}`;
  await drawProFooter(doc, {
    settings, doctor,
    qrUrl: verifyUrl,
    qrLabel: "Verificar historia",
    sigLabel: "Firma y sello del profesional",
    signature: it.signature,
    patientSignature: {
      name: it.patient_name || "",
      nif: it.patient_nif || "",
      consent: !!it.consent_given,
      label: "Firma del Paciente / Consentimiento Informado",
    },
  });

  doc.save(`interrogatorio-${it.code}.pdf`);
}
